// Locating a search-hit snippet inside the rendered PDF text.
//
// Snippets are raw document text with <em> markers around the matched term. Their
// whitespace never lines up with the PDF text layer (line breaks, runs of spaces), so we
// anchor on short windows of the snippet, then highlight the <em> term nearest to where
// that anchor says it should be.

const WINDOW_SIZE = 15;
const MIN_CANDIDATE = 5;
// How far a term may sit from its expected position before we reject it — absorbs
// whitespace differences between the snippet and the PDF text layer.
const OFFSET_TOLERANCE = 250;

export interface SnippetPart {
  text: string;
  offset: number; // char index in the plain (em-stripped) snippet
}

export interface SnippetSearch {
  candidates: SnippetPart[]; // anchor windows, in snippet order
  terms: SnippetPart[];      // one per <em> run
}

export interface PageText {
  pageNum: number;
  text: string;
}

export interface SnippetMatch {
  pageNum: number;
  start: number;
  end: number;
}

export function parseSnippet(snippetHtml: string): { plain: string; terms: SnippetPart[] } {
  const terms: SnippetPart[] = [];
  const re = /<em>([\s\S]*?)<\/em>/g;
  let plain = '';
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(snippetHtml)) !== null) {
    plain += snippetHtml.slice(last, m.index);
    if (m[1].trim()) terms.push({ text: m[1], offset: plain.length });
    plain += m[1];
    last = m.index + m[0].length;
  }
  plain += snippetHtml.slice(last);
  return { plain, terms };
}

export function buildSnippetSearch(snippetHtml: string): SnippetSearch {
  const { plain, terms } = parseSnippet(snippetHtml);
  const candidates: SnippetPart[] = [];
  let i = 0;
  while (i < plain.length) {
    while (i < plain.length && (plain[i] === '\n' || plain[i] === '\r')) i++;
    if (i >= plain.length) break;

    let end = i;
    while (end < plain.length && end - i < WINDOW_SIZE && plain[end] !== '\n' && plain[end] !== '\r') end++;

    const raw = plain.slice(i, end);
    const text = raw.trim();
    if (text.length >= MIN_CANDIDATE) candidates.push({ text, offset: i + raw.indexOf(text) });
    i = end;
  }
  return { candidates, terms };
}

function findOccurrences(pages: PageText[], needle: string): SnippetMatch[] {
  const out: SnippetMatch[] = [];
  if (!needle) return out;
  for (const page of pages) {
    let pos = page.text.indexOf(needle);
    while (pos !== -1) {
      out.push({ pageNum: page.pageNum, start: pos, end: pos + needle.length });
      pos = page.text.indexOf(needle, pos + 1);
    }
  }
  return out;
}

/**
 * Resolves a snippet against the rendered page text.
 *
 * Returns the matches to highlight and the query to show in the find bar. With <em>
 * terms, those are the highlights; without them (or when none can be placed), the
 * anchor window is highlighted as before.
 */
export function resolveSnippetMatches(
  search: SnippetSearch,
  pages: PageText[],
): { query: string; matches: SnippetMatch[] } {
  const lowerPages = pages.map(p => ({ pageNum: p.pageNum, text: p.text.toLowerCase() }));

  for (const cand of search.candidates) {
    const hits = findOccurrences(lowerPages, cand.text.toLowerCase());
    if (hits.length === 0) continue;

    const anchor = hits[0];
    if (search.terms.length === 0) return { query: cand.text, matches: hits };

    const page = lowerPages.find(p => p.pageNum === anchor.pageNum)!;
    const termMatches: SnippetMatch[] = [];
    for (const term of search.terms) {
      const expected = anchor.start + (term.offset - cand.offset);
      const needle = term.text.toLowerCase();
      let best: SnippetMatch | null = null;
      let bestDist = Infinity;
      let pos = page.text.indexOf(needle);
      while (pos !== -1) {
        const dist = Math.abs(pos - expected);
        if (dist < bestDist) {
          bestDist = dist;
          best = { pageNum: page.pageNum, start: pos, end: pos + needle.length };
        }
        pos = page.text.indexOf(needle, pos + 1);
      }
      if (best && bestDist <= OFFSET_TOLERANCE) termMatches.push(best);
    }

    const unique = termMatches
      .filter((m, i) => termMatches.findIndex(o => o.start === m.start) === i)
      .sort((a, b) => a.start - b.start);

    if (unique.length > 0) return { query: search.terms[0].text, matches: unique };
    return { query: cand.text, matches: hits };
  }

  return { query: search.candidates[0]?.text ?? '', matches: [] };
}
